/**
 * Asset Helper Functions
 *
 * Utility functions for asset operations
 */

import { getAuthHeader } from "@/config/api";
import { getAccessToken } from "@/services/auth/clientSession";
import { authService } from "../auth/index";
import { GroupedAssets, UserAsset } from "./types";
import { dedupeInFlightGet, clearInFlightGetDedup } from "@/utils/inFlightGetDedup";
import {
  PRODUCTION_TEST_LABEL_MESSAGE,
  isUserFacingAssetServiceError,
} from "@/utils/productionUserContentValidation";

/** Expected validation or business-rule rejection — not a crash; avoid Sentry/console.error noise. */
export class AssetServiceUserError extends Error {
  readonly userFacing = true as const;

  constructor(
    message: string,
    public readonly code: string
  ) {
    super(message);
    this.name = "AssetServiceUserError";
  }
}

export function authHeaders(): Promise<Record<string, string>> {
  return authService.getAuthHeaders();
}

/**
 * Resolve Api Request Headers.
 */
export function resolveApiRequestHeaders(
  options: globalThis.RequestInit,
  token: string | null | undefined
): Promise<Record<string, string>> {
  const isStateChangingOperation = ["POST", "PUT", "DELETE", "PATCH"].includes(
    options.method || "GET"
  );

  if (isStateChangingOperation) {
    return authService.getAuthHeaders();
  }

  const authHeaders = getAuthHeader(token || undefined);
  if (!options.headers) {
    return Promise.resolve(authHeaders);
  }

  return Promise.resolve({ ...authHeaders, ...options.headers });
}

/**
 * Get authentication token from Supabase session
 */
export function getAuthToken(): Promise<string | null> {
  return getAccessToken();
}

/**
 * Log asset service operations for debugging
 */
export function logOperation(operation: string, details: Record<string, unknown>) {
  if (import.meta.env.DEV) {
    console.warn("[AssetService]", operation, details);
  }
}

/**
 * Handle API errors consistently
 */
function toUserFacingAssetServiceError(errorMessage: string): AssetServiceUserError | null {
  if (isUserFacingAssetServiceError(new Error(errorMessage))) {
    if (
      errorMessage.includes("Duplicate bank account") ||
      errorMessage.includes("Another asset already uses this bank")
    ) {
      return new AssetServiceUserError(errorMessage, "DUPLICATE_BANK_BALANCE");
    }

    return new AssetServiceUserError(PRODUCTION_TEST_LABEL_MESSAGE, "PROD_DATA_GUARD");
  }

  return null;
}

/**
 * Handle Api Error.
 */
export function handleApiError(operation: string, error: unknown): never {
  if (error instanceof AssetServiceUserError) {
    throw error;
  }

  let errorMessage = "Unknown error occurred";
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (
    typeof error === "object" &&
    error !== null &&
    ("message" in error || "error" in error)
  ) {
    errorMessage =
      ("message" in error && typeof error.message === "string" ? error.message : "") ||
      ("error" in error && typeof error.error === "string" ? error.error : "") ||
      errorMessage;
  }

  const userFacingError = toUserFacingAssetServiceError(errorMessage);
  if (userFacingError) {
    throw userFacingError;
  }

  logOperation(`${operation} FAILED`, { error: errorMessage });
  throw new Error(`Asset service error (${operation}): ${errorMessage}`);
}

/**
 * Map backend asset type keys (snake_case) to frontend camelCase keys
 */
export function mapBackendKeysToFrontend(backendData: Record<string, UserAsset[]>): GroupedAssets {
  const mapping: Record<string, keyof GroupedAssets> = {
    gold: "gold",
    pf: "pf",
    ppf: "ppf",
    vpf: "vpf",
    insurance: "insurance",
    stocks: "stocks",
    mutual_funds: "mutualFunds",
    mutualFunds: "mutualFunds", // Handle both cases
    bonds: "bonds",
    real_estate: "realEstate",
    realEstate: "realEstate", // Handle both cases
    crypto: "crypto",
    commodities: "commodities",
    fixed_deposits: "fixedDeposits",
    fixedDeposits: "fixedDeposits", // Handle both cases
    bank_balance: "bankBalance",
    bankBalance: "bankBalance", // Handle both cases
    other: "generalAssets",
    general: "generalAssets",
  };

  const frontendData: GroupedAssets = {
    gold: [],
    pf: [],
    ppf: [],
    vpf: [],
    insurance: [],
    stocks: [],
    mutualFunds: [],
    bonds: [],
    realEstate: [],
    crypto: [],
    commodities: [],
    fixedDeposits: [],
    bankBalance: [],
    generalAssets: [],
  };

  // Map backend keys to frontend keys
  for (const [backendKey, assets] of Object.entries(backendData)) {
    const frontendKey = mapping[backendKey] || "generalAssets";
    frontendData[frontendKey] = frontendData[frontendKey] || [];
    frontendData[frontendKey].push(...assets);
  }

  return frontendData;
}

/** Deduplicate concurrent in-flight GET requests only — React Query owns response freshness. */
export function resetActiveAssetGetRequestsForTests(): void {
  clearInFlightGetDedup();
}

/**
 * Clears any HTTP GET dedup state. Safe no-op today (no response TTL cache); kept so
 * query invalidation helpers can bust transport-level caches if reintroduced later.
 */
export function clearApiGetResponseCache(): void {
  clearInFlightGetDedup();
}

/**
 * Make authenticated API call
 */
export function apiCall<T>(endpoint: string, options: globalThis.RequestInit = {}): Promise<T> {
  const method = options.method || "GET";

  /**
   * Execute Request.
   */
  const executeRequest = async (): Promise<T> => {
    const token = await getAuthToken();

    const headers = await resolveApiRequestHeaders(options, token);

    const response = await fetch(endpoint, {
      ...options,
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({
        error: response.statusText,
      }))) as { error?: string; code?: string };
      const message = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      const code = errorData.code || "API_ERROR";

      if (
        code === "PROD_DATA_GUARD" ||
        code === "INVALID_ASSET_NAME" ||
        code === "DUPLICATE_BANK_BALANCE"
      ) {
        const userMessage = code === "PROD_DATA_GUARD" ? PRODUCTION_TEST_LABEL_MESSAGE : message;
        throw new AssetServiceUserError(userMessage, code);
      }

      throw new Error(message);
    }

    return response.json();
  };

  if (method === "GET") {
    return dedupeInFlightGet(endpoint, executeRequest);
  }

  return executeRequest();
}
